import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  CheckCircle as ValidateIcon,
  Delete as DeleteIcon,
  ConfirmationNumber as TicketIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import ResponsivePageHeader from '../../components/Common/ResponsivePageHeader';
import { passkitAPI } from '../../services/api';
import { logger } from '../../utils/logger';

interface Production {
  id: string;
  name: string;
  description?: string;
  status: string;
  passkit_production_id: string;
  company_id: string;
}

interface Ticket {
  id: string;
  ticketNumber: string;
  person?: {
    forename?: string;
    surname?: string;
    emailAddress?: string;
  };
  status?: string;
  event?: {
    scheduledStartDate?: string;
  };
  ticketType?: {
    name?: string;
  };
  seatInfo?: {
    seat?: string;
    row?: string;
    section?: string;
  };
  redemptionDetails?: {
    lat?: number;
    lon?: number;
    alt?: number;
    redemptionDate?: string;
    redemptionSource?: string;
  };
}

const Tickets: React.FC = () => {
  // Data states
  const [productions, setProductions] = useState<Production[]>([]);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [selectedProduction, setSelectedProduction] = useState<Production | null>(null);

  // UI states
  const [loading, setLoading] = useState(false);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Dialog states
  const [validateDialogOpen, setValidateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);

  const loadTicketsForProduction = async (production: Production) => {
    setLoadingTickets(true);
    setError(null);

    try {
      // Use passkit_production_id since the backend can accept it directly
      const response = await passkitAPI.listTickets(production.passkit_production_id);
      logger.log('🎫 Loaded tickets:', response);
      setTickets(response.tickets || []);
    } catch (err: any) {
      logger.error('Error loading tickets:', err);
      setError(err.response?.data?.detail || 'Greška pri učitavanju karata');
      setTickets([]);
    } finally {
      setLoadingTickets(false);
    }
  };

  const loadProductions = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await passkitAPI.listProductions();
      logger.log('📦 Loaded productions:', response);
      setProductions(response.productions || []);

      if (response.productions && response.productions.length > 0) {
        setSelectedProduction(response.productions[0]);
        loadTicketsForProduction(response.productions[0]);
      }
    } catch (err: any) {
      logger.error('Error loading productions:', err);
      setError(err.response?.data?.detail || 'Greška pri učitavanju produkcija');
    } finally {
      setLoading(false);
    }
  }, []); // Empty dependency array since it doesn't depend on any props or state

  useEffect(() => {
    loadProductions();
  }, [loadProductions]);

  const handleProductionChange = (production: Production) => {
    setSelectedProduction(production);
    loadTicketsForProduction(production);
  };

  const handleValidateClick = (ticket: Ticket) => {
    setSelectedTicket(ticket);
    setValidateDialogOpen(true);
  };

  const handleDeleteClick = (ticket: Ticket) => {
    setSelectedTicket(ticket);
    setDeleteDialogOpen(true);
  };

  const handleValidateConfirm = async () => {
    if (!selectedTicket || !selectedProduction) return;

    try {
      await passkitAPI.validateTicket(selectedProduction.passkit_production_id, selectedTicket.id);
      setSuccessMessage('Karta je uspešno validirana!');
      setValidateDialogOpen(false);
      setSelectedTicket(null);

      // Reload tickets
      if (selectedProduction) {
        loadTicketsForProduction(selectedProduction);
      }
    } catch (err: any) {
      logger.error('Error validating ticket:', err);
      setError(err.response?.data?.detail || 'Greška pri validaciji karte');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedTicket || !selectedProduction) return;

    try {
      await passkitAPI.deleteTicket(selectedProduction.passkit_production_id, selectedTicket.id);
      setSuccessMessage('Karta je uspešno obrisana!');
      setDeleteDialogOpen(false);
      setSelectedTicket(null);

      // Reload tickets
      if (selectedProduction) {
        loadTicketsForProduction(selectedProduction);
      }
    } catch (err: any) {
      logger.error('Error deleting ticket:', err);
      setError(err.response?.data?.detail || 'Greška pri brisanju karte');
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('sr-RS', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <ResponsivePageHeader
        title="Karte"
        subtitle="Pregled i upravljanje izdatim kartama"
        icon={<TicketIcon sx={{ fontSize: 40 }} />}
        actions={
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => selectedProduction && loadTicketsForProduction(selectedProduction)}
            disabled={!selectedProduction || loadingTickets}
          >
            Osveži
          </Button>
        }
      />

      {/* Production Selector */}
      {!loading && productions.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Izaberi Produkciju
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {productions.map((production) => (
              <Chip
                key={production.id}
                label={production.name}
                onClick={() => handleProductionChange(production)}
                color={selectedProduction?.id === production.id ? 'primary' : 'default'}
                variant={selectedProduction?.id === production.id ? 'filled' : 'outlined'}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>
          {selectedProduction && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Izabrana produkcija:</strong> {selectedProduction.name}
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tickets Statistics */}
      {selectedProduction && !loadingTickets && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Paper sx={{ p: 2, flex: '1 1 200px', minWidth: '200px', textAlign: 'center' }}>
            <Typography variant="h4" color="primary">{tickets.length}</Typography>
            <Typography variant="body2">Ukupno karata</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: '1 1 200px', minWidth: '200px', textAlign: 'center' }}>
            <Typography variant="h4" color="success.main">
              {tickets.filter(t => t.redemptionDetails?.redemptionDate).length}
            </Typography>
            <Typography variant="body2">Validirane</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: '1 1 200px', minWidth: '200px', textAlign: 'center' }}>
            <Typography variant="h4" color="warning.main">
              {tickets.filter(t => !t.redemptionDetails?.redemptionDate).length}
            </Typography>
            <Typography variant="body2">Nevalidirane</Typography>
          </Paper>
        </Box>
      )}

      {/* Loading State */}
      {loading || loadingTickets ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Tickets Table */}
          {selectedProduction && (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Broj Karte</strong></TableCell>
                    <TableCell><strong>ID Karte</strong></TableCell>
                    <TableCell><strong>Događaj</strong></TableCell>
                    <TableCell><strong>Sedište</strong></TableCell>
                    <TableCell><strong>Datum Događaja</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell align="right"><strong>Akcije</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tickets.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Box sx={{ py: 3 }}>
                          <Typography variant="body2" color="textSecondary">
                            Nema izdatih karata za ovu produkciju.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    tickets.map((ticket) => (
                      <TableRow key={ticket.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {ticket.ticketNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.75rem',
                              color: 'text.secondary'
                            }}
                          >
                            {ticket.id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {ticket.ticketType?.name || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {ticket.seatInfo?.row || ticket.seatInfo?.seat ? (
                            <Typography variant="body2">
                              Red {ticket.seatInfo.row}, Sedište {ticket.seatInfo.seat}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(ticket.event?.scheduledStartDate)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {ticket.redemptionDetails?.redemptionDate ? (
                            <Chip
                              label="Validirana"
                              size="small"
                              color="success"
                              icon={<ValidateIcon />}
                            />
                          ) : (
                            <Chip
                              label="Aktivna"
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {!ticket.redemptionDetails?.redemptionDate && (
                            <Tooltip title="Validiraj Kartu">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleValidateClick(ticket)}
                              >
                                <ValidateIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Obriši Kartu">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(ticket)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* Validate Confirmation Dialog */}
      <Dialog open={validateDialogOpen} onClose={() => setValidateDialogOpen(false)}>
        <DialogTitle>Potvrdi Validaciju</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Da li ste sigurni da želite da validirate kartu <strong>{selectedTicket?.ticketNumber}</strong>?
            {selectedTicket?.person && (
              <>
                <br />
                Putnik: {selectedTicket.person.forename} {selectedTicket.person.surname}
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValidateDialogOpen(false)}>Otkaži</Button>
          <Button
            onClick={handleValidateConfirm}
            variant="contained"
            color="success"
            autoFocus
            sx={{ color: 'white' }}
          >
            Validiraj
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Potvrdi Brisanje</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Da li ste sigurni da želite da obrišete kartu <strong>{selectedTicket?.ticketNumber}</strong>?
            {selectedTicket?.person && (
              <>
                <br />
                Putnik: {selectedTicket.person.forename} {selectedTicket.person.surname}
              </>
            )}
            <br /><br />
            <strong>Napomena:</strong> Ova akcija je nepovratna i karta će biti poništena u wallet-u korisnika.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Otkaži</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error" autoFocus>
            Obriši
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Tickets;
